import type { ProblemCopy } from '../../api/problem.ts';
import { unreadable } from './shared.ts';

/**
 * §8's Watch page and §7's player chrome — the largest section, and the one where a translated word
 * has to survive being read *while a video is playing*.
 *
 * Two groups are worth reading before changing anything. The **unplayable** sentences each name a
 * distinct state a reader can act on differently; collapsing any two of them into "cannot play this
 * video" throws away the only information the page has, and every one of them is a playback problem, so
 * they say what is wrong with the playback rather than only that something is. And the **menu** values
 * (`Auto (1080p)`, `1.5×`, `720p`) are assembled from numbers here rather than at the call site, so a
 * language that puts the unit first, or writes its decimals differently, can say so.
 */
export const watch = {
  /**
   * §7's list, in the app's words. `no-source` is the one that needs explaining rather than
   * apologising for: it is the captured state where a live/DVR video has nothing playable behind it at
   * all, so there is genuinely nothing to play — not a bug on our side, and not "try again".
   */
  unplayable: {
    region: {
      title: 'Not available in this region',
      detail:
        'The uploader restricted this video to countries that do not include the one set in Settings.',
    },
    paid: {
      title: 'Paid or members-only',
      detail: 'This video is behind a purchase or a channel membership, which Visious cannot unlock.',
    },
    private: {
      title: 'Private video',
      detail: 'Only people the uploader invited can watch this one.',
    },
    removed: { title: 'Video removed', detail: 'YouTube no longer serves this video.' },
    noMse: {
      title: 'This browser cannot play adaptive video',
      detail:
        'It does not support Media Source Extensions, which adaptive video needs, and this video offers no simpler stream to fall back to.',
    },
    noSource: {
      title: 'No playable stream',
      detail: 'Your instance lists this video as available and then has nothing playable behind it.',
    },
    unknownTitle: 'Cannot play this video',
    unknownDetail: (reason: string): string => `Your instance reports it as unplayable: ${reason}.`,
  },

  /** §7's no-MSE row: a single fixed stream, with the limitation stated rather than hidden. */
  progressiveNotice:
    'This browser cannot play adaptive video, so this is a single 360p stream. Quality, audio-language and subtitle controls do not work on it.',

  /** §3's instance resolution failing is a *configuration* problem, not a request problem. */
  bootstrap: {
    badInstanceTitle: 'The instance address will not work',
    badInstanceDetail: (url: string): string =>
      `${url} is not a usable address. Check the instance under Settings.`,
    /** What stands in for the URL when the problem carried none. */
    badInstanceFallback: 'It',
    noInstanceTitle: 'No instance configured',
    noInstanceDetail: 'Visious needs an Invidious instance to talk to. Choose one under Settings.',
  },

  problem: {
    notFound: {
      title: 'Video not found',
      detail: 'The instance has no such video. It may have been removed, or the link may be wrong.',
      retryable: false,
    },
    /**
     * The four sentences that replace {@link notFound} where YouTube said what became of the video
     * (`videoGoneSubject`). Its second half, "the link may be wrong", is the part that has to go: the
     * link was right, and telling a reader to check it sends them looking for a mistake they did not
     * make.
     */
    removed: {
      title: 'This video was removed',
      detail: 'It is no longer on YouTube. Either the uploader took it down or YouTube did.',
      retryable: false,
    },
    private: {
      title: 'This video is private',
      detail: 'It is still on YouTube, and only the people the uploader shared it with can watch it.',
      retryable: false,
    },
    /**
     * No instruction to go to YouTube in the sentence: the failure box draws the whole "Watch on"
     * menu right under it, so the way there is already on screen.
     */
    ageRestricted: {
      title: 'This video is age-restricted',
      detail: 'YouTube serves it only to a signed-in account, and your instance does not have one.',
      retryable: false,
    },
    membersOnly: {
      title: 'This video is members-only',
      detail: 'YouTube serves it only to members of this channel, and your instance is not a member.',
      retryable: false,
    },
    /**
     * The fifth sentence that replaces {@link notFound}, and the only one about a video that is fine.
     * A scheduled live stream is a date rather than a defect, so the words say when and what to do
     * instead of what went wrong. `when` is the wait already in this language ("in 3 hours"), or
     * `null` where the instance's sentence carried no number.
     *
     * **The one retryable member of this group**, and the retry is the point: a stream that begins in
     * a few moments is watchable from this page a moment later, and the Watch on menu beside the
     * button leads to YouTube's own waiting room for the longer waits.
     */
    notYetLive: (when: string | null): ProblemCopy => ({
      title: 'This live stream has not started yet',
      detail:
        when === null ?
          'Try again once it has begun.'
        : `It starts ${when}. Try again once it has begun.`,
      retryable: true,
    }),
    unreadable: unreadable('Could not read the video'),
  },

  /**
   * One dropdown over both ways out of this player: the rows that replace the picture where it
   * stands, then the rows that are links to the video somewhere else. The two headings are what
   * tells them apart, so they appear only while both sections do.
   */
  watchOn: {
    label: 'Watch on',
    /** The first section: swap the player, stay on the page. */
    switchPlayer: 'Switch player',
    /** The second: follow a link to the video somewhere else. */
    openSite: 'Open site',
    targets: {
      youtube: 'YouTube',
      instance: 'Invidious',
      peertube: 'PeerTube',
    },
    /** The way back, under Switch player — the destination now driving the picture wears this. */
    visious: 'Invidious',
    /** The checkbox row under both sections — `time` is the playhead, already formatted. */
    atTime: (time: string): string => `Start at ${time}`,
    /** A PeerTube row's right-hand label on a miss — a hit shows the match's instance host. */
    peerTubeNotFound: 'No video found',
    /** The same row when the search cannot be made: the video's title and length are not known. */
    peerTubeNoInfo: 'No video info',
  },

  /** The Share menu: pressing a destination puts that link on the clipboard. */
  share: {
    label: 'Share link',
    targets: {
      visious: 'Visious',
      instance: 'Invidious',
      youtube: 'YouTube',
      /** The fourth destination, offered while the PeerTube fallback is on: the matched video. */
      peertube: 'PeerTube',
    },
    /** The checkbox row under the destinations — `time` is the playhead, already formatted. */
    atTime: (time: string): string => `Start at ${time}`,
    /** The PeerTube row's right-hand label on a miss — a hit shows the match's instance host. */
    peerTubeNotFound: 'No video found',
    /** The same row when the search cannot be made: the video's title and length are not known. */
    peerTubeNoInfo: 'No video info',
    copied: 'Link copied',
    copyFailed: 'Could not copy the link',
  },

  /** The page itself — the stage, the byline, the description and the rail. */
  page: {
    playerLabel: 'Video player',
    /** The stage's loading line, shared with the embed view — a JSX text node in both until now. */
    loadingVideo: 'Loading this video…',
    /** The iframe's accessible name — the Watch on menu's two embedded legs. */
    externalFrameTitle: {
      youtube: 'YouTube video player',
      peertube: 'PeerTube video player',
    },
    premiereHeading: 'Premiere',
    premiereUnknown: 'This video has not premiered yet.',
    premiereAt: (when: string): string => `Premieres ${when}.`,
    /**
     * The three lines of `/embed/?list=PL…`, the address that names a playlist and no video. It
     * reads the list before it knows what it is showing, so it says so, and it says where to go
     * when the list cannot be read at all.
     */
    listLoading: 'Loading this playlist…',
    listProblem: 'This playlist could not be loaded.',
    listEmpty: 'This playlist has no videos in it.',
    listOpen: 'Open this playlist',
    playBlocked: 'The browser blocked playback. Press play to start it.',
    mutedFallback: 'The browser only allowed muted playback. Unmute when you are ready.',
    verified: 'Verified',
    views: (count: string): string => `${count} views`,
    likes: (count: string): string => `${count} likes`,
    /** Return YouTube Dislike's counter, right of the like one — off until the reader enables it. */
    dislikes: (count: string): string => `${count} dislikes`,
    /** Under the author's name — the channel page's own figure, same wording. */
    subscribers: (count: string): string => `${count} subscribers`,
    showMore: 'Show more',
    showLess: 'Show less',
    chapters: 'Chapters',
    show: 'Show',
    /** The panel toggles: "Hide" is the visible word, the full phrase is the accessible name. */
    hide: 'Hide',
    hideChapters: 'Hide chapters',
    showChapters: 'Show chapters',
    playerFailedTitle: 'Could not load the video',
    playerFailed: 'The player could not load this video.',
    /** The engine's own chunk did not arrive, which a new deploy under an open tab can cause. */
    playerCodeFailed: 'The player could not be loaded. Reload the page.',
  },

  /**
   * The minimized player. Four labels, and none of them appears as visible text: the box is a
   * picture with icons on it, so every one of these is the accessible name of a control or of the
   * region itself.
   */
  mini: {
    /** Names the whole box, which is a `region` landmark sitting outside the routed page. */
    label: 'Minimized player',
    /** Not "Fullscreen" and not "Restore": it goes back to the watch page, playing, where it was. */
    maximize: 'Back to the video',
    close: 'Close the minimized player',
    /**
     * The same ✕ while a queue is running, which the press also ends (2026-08-08). The box is the
     * queue's window, so putting it away is what clears the list, and a label that only said "close"
     * would be describing the smaller half of what happens.
     */
    closeQueue: 'Close the player and clear the queue',
    /**
     * The two steps through the list either side of Play. They say what the press does to the corner
     * player, since that is where it happens: the page the reader is on does not change.
     */
    previous: 'Play the previous video here',
    next: 'Play the next video here',
    /**
     * A load that failed while the box was minimized. It says where the detail is rather than trying
     * to fit it: the full message, Try again and the Watch on menu are all on the watch page, and a
     * 320 px box that tried to hold them would hold none of them legibly.
     */
    failed: 'Playback stopped. Open the video for details',
    /**
     * A video the corner player has no picture for stands its own refusal in the same place
     * (`miniStageNotice`), so these sentences are the stage's own. This is the one state with no
     * sentence of its own to borrow: the no-MSE fallback is the browser's plain element on the watch
     * page rather than this player, so a queue that reaches such a video has nothing to put in the
     * box.
     */
    pageOnly: 'This video plays only on its own page.',
  },

  /** §7's chrome — every one of these is an `aria-label` on a control with no visible text. */
  chrome: {
    seek: 'Seek',
    /** The scrub bar's `aria-valuetext` — the one place the chrome speaks a whole phrase. */
    position: (current: string, duration: string): string => `${current} of ${duration}`,
    /** The badge that stands where the elapsed/total pair goes on a live stream. */
    live: 'LIVE',
    play: 'Play',
    pause: 'Pause',
    back10: 'Back 10 seconds',
    forward10: 'Forward 10 seconds',
    mute: 'Mute',
    unmute: 'Unmute',
    volume: 'Volume',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    theater: 'Theater mode',
    exitTheater: 'Exit theater mode',
    /**
     * The widen control, offered on a 16∶9 video in theater. The words name the picture's new shape
     * and not the crop, because that is what the reader is choosing; what it costs — a strip off the
     * top and the bottom — is on screen the moment they press it.
     */
    widen: 'Widen the picture to 21:9',
    exitWiden: 'Back to the full 16:9 picture',
    /**
     * The bar's chapter name, which presses through to the chapter list. `title` is the chapter's
     * own name, so the accessible name carries the visible words a voice command would say.
     */
    showChapter: (title: string): string => `Show “${title}” in the chapter list`,
    /**
     * What a skip says while it is happening. The tap gesture's disc has one line inside a
     * circle, so it prints the unit short and **unspaced** — a gap inside a two-character label reads
     * as a gap in the label, not as SI punctuation; the toast a control press raises is a sentence, and
     * it says which way — the disc has its side of the picture to say that.
     */
    skipDisc: (seconds: number): string => `${seconds}s`,
    skipBack: (seconds: number): string => `Back ${seconds} seconds`,
    skipForward: (seconds: number): string => `Forward ${seconds} seconds`,
  },

  /**
   * The SponsorBlock surfaces on this page: the two toasts and the chapter badge's hover title.
   * The category names themselves come from the shared `sponsorBlock` section.
   */
  sponsor: {
    /** The un-skip toast: what just happened, named. "Skipped Sponsor". */
    skipped: (category: string): string => `Skipped ${category}`,
    unskip: 'Unskip',
    /** The offer toast is the category's name and this button. */
    skip: 'Skip',
    /** The chapter badge's hover title — the badge itself is the bare category name. */
    badgeTitle: (category: string): string =>
      `Marked by SponsorBlock: ${category} covers most of this chapter.`,
  },

  comments: {
    sectionLabel: 'Comments',
    sortLabel: 'Sort comments',
    pinned: 'Pinned',
    member: 'Member',
    /**
     * The two glyphs in a comment's footer are drawn, so these are what a pointer and a screen
     * reader get for them. `heartedBy` takes the channel's name where the payload carries one and
     * `heartedByCreator` stands in where it does not — a name is the useful half of the fact.
     */
    likes: 'Likes',
    heartedBy: (channel: string): string => `Hearted by ${channel}`,
    heartedByCreator: 'Hearted by the creator',
    /** The marker after a comment's timestamp. Parenthesised because it qualifies the time beside it. */
    edited: '(edited)',
    /** One thread's replies failed while the rest of the section is fine, so it says so in place. */
    repliesFailed: 'Could not load these replies.',
    repliesRetry: 'Try again',
    showMore: 'Show more comments',
    loading: 'Loading comments…',
    loadingMore: 'Loading…',
    loadingReplies: 'Loading replies…',
    /** The fold-away: the heading and its count stay, the body and the sort go. */
    section: {
      hide: 'Hide',
      hideLabel: 'Hide the comments',
      show: 'Show',
      showLabel: 'Show the comments',
    },
  },

  rail: {
    label: 'Recommended videos',
    heading: 'Recommended',
    hide: 'Hide',
    show: 'Show',
    hideRecommendations: 'Hide recommendations',
    showRecommendations: 'Show recommendations',
    /** The pager under the four-row window — round < > glyphs; these are their spoken names. */
    previousLabel: 'Previous recommendations',
    nextLabel: 'More recommendations',
    pageOf: (page: number, pages: number): string => `${page} / ${pages}`,
  },

  /**
   * The playlist panel — the side column's third section, above chapters, present only when the
   * route carries a `list`. "Autoplay" is the word YouTube taught everyone; the detail says which
   * autoplay this is, since the Playback tab has one of its own about starting *this* video.
   */
  playlist: {
    label: 'Playlist',
    heading: 'Playlist',
    hide: 'Hide',
    show: 'Show',
    hideList: 'Hide the playlist',
    showList: 'Show the playlist',
    position: (at: number, total: number): string => `${at} of ${total}`,
    autoplay: 'Autoplay',
    autoplayDetail: 'Autoplay the next video in this playlist when this one ends',
    /**
     * The order switch beside Autoplay, and the same per-playlist choice the playlist page's Order
     * menu makes. The detail says what reversing does to playback, not only to the list, because it
     * moves the back and forward buttons and Autoplay along with the rows.
     */
    reverse: 'Reverse the order',
    reverseDetail: 'Reverse the playlist order.',
    /**
     * Leaving the playlist, at the far end of the controls row. It names what happens to the panel
     * rather than to the record, because the record is what a reader might fear for here: nothing is
     * sent, so the detail says the two things that stay.
     */
    clear: 'Clear the playlist',
    clearDetail: 'Stop watching this playlist.',
    /**
     * Between Clear and the order switch: keep the videos and let go of the playlist behind them. Its
     * detail says all three things a reader has to know before pressing it, since the act cannot be
     * pressed back: what the list becomes, that the unread rest of the playlist is not fetched, and
     * that the playlist itself is untouched.
     */
    unlink: 'Unlink',
    unlinkDetail: 'Unlink the current queue from the playlist, so changes will no longer be saved to it.',
    /** The two entry buttons either side of Autoplay. Not the pager below, which turns pages. */
    previousEntry: 'Previous video in this playlist',
    nextEntry: 'Next video in this playlist',
    /** The pager under the four-row window — round < > glyphs; these are their spoken names. */
    previousLabel: 'Previous entries',
    nextLabel: 'More entries',
    nowPlaying: 'Now playing',
    entryGone: 'Video unavailable',
    /** The row's remove button: the video in the accessible name, the act alone as the hover word. */
    remove: (title: string): string => `Remove “${title}” from this playlist`,
    removeTitle: 'Remove from this playlist',
    removing: 'Removing…',
    loading: 'Loading the playlist…',
    problem: 'The playlist did not load. The video plays either way.',
  },

  /**
   * The queue panel. The same panel the playlist draws, wearing the words for a list that is this
   * tab's own: nothing here says "playlist", because a queue is not saved anywhere and calling it
   * one would promise that it is.
   *
   * The **head** is the exception, and it declares no words of its own for that reason. On the watch
   * page the queue is the side column's list, so it is named the way that column names a list:
   * "Playlist" over it and "Show the playlist" on the two controls that bring it back. What the
   * reader has open is still said, one line lower, by the heading that reads "Queue". Every word
   * below is a row's or a control's, and those stay the queue's.
   *
   * Autoplay and Reverse have their own words here rather than borrowing the playlist's, because both
   * mean something different on this list: the queue's Autoplay is a second stored switch, and its
   * Reverse turns the entries around for real instead of only flipping what is drawn.
   */
  queue: {
    /**
     * Auto-advance, the queue's own. On by default, which is why the detail says what switching it off
     * gets you rather than what leaving it on does.
     */
    autoplay: 'Autoplay',
    autoplayDetail:
      'Play the next video in the queue when this one ends. Switch it off to stop after every video.',
    /**
     * The order switch, which on a queue is an act and not a state: the entries really change places,
     * so the panel draws them in the new order and there is no flipped list to mark as flipped.
     */
    reverse: 'Reverse the order',
    reverseDetail: 'This turns the whole queue around. The video that is playing stays on screen.',
    /**
     * Empties the list, on one press like everything else on this row. The detail carries the stakes,
     * since there is no armed face here to put them on and the press cannot be taken back.
     */
    clear: 'Clear the queue',
    clearDetail: 'This empties the queue right away. The list is not saved anywhere else.',
    /**
     * The one control on this row that reaches outside the tab: it hands the whole list to a playlist
     * on the account. Its `detail` is the sentence the queue never had anywhere on screen — a list
     * that dies with the tab, and the way to keep it — so it is the tooltip of the control that acts
     * on that fact rather than a line of prose in the panel.
     */
    save: {
      label: 'Save the queue as a playlist',
      detail: 'The queue is tied to this browser tab. Saving it to a playlist is how you keep it.',
      /** The dialog: the reader's own playlists, and the create form under them. */
      title: 'Save the queue as a playlist',
      close: 'Close',
      lede: 'Every video in the queue that the playlist does not hold yet is added to its end. The list then plays in the playlist’s own order.',
      signedOutBefore: 'Playlists live in your account on the instance, so this needs you signed in.',
      signIn: 'Sign in',
      signedOutAfter: 'and you will come straight back here.',
      loading: 'Loading your playlists…',
      none: 'You have no playlists yet. The first one is a title away.',
      /** The create button's promise here is bigger than "Create": it saves the whole queue too. */
      createSubmit: 'Create & save',
      /** A long playlist has to be read before anything is sent, or a video goes in twice. */
      reading: 'Reading the playlist…',
      saving: 'Saving…',
      /**
       * What the save did, on the queue's own outcome line. Both numbers are in one sentence because
       * the pair is the answer: a reader saving six videos into a playlist that already held two wants
       * to know that four went across and that nothing was duplicated.
       */
      saved: (videos: string, already: number, name: string): string =>
        already === 0
          ? `Saved ${videos} to “${name}”.`
          : already === 1
            ? `Saved ${videos} to “${name}”, and one was already in it.`
            : `Saved ${videos} to “${name}”, and ${already} were already in it.`,
      /** Nothing to send, which is a result rather than a failure: the playlist is already the list. */
      nothingToSave: (name: string): string => `“${name}” already holds every video in the queue.`,
      /**
       * A refusal, with the write's own sentence leading (`playlists/model.ts`) exactly as a card's
       * refused add has it. The second arm is the one that matters: the adds are a request each, so a
       * refusal partway through leaves part of the list on the playlist, and a reader told "saved"
       * about half a list has been told something false.
       */
      refused: (reason: string): string => `${reason}. The playlist was not changed.`,
      refusedAfter: (reason: string, videos: string, name: string): string =>
        `${reason}. Only ${videos} reached “${name}”.`,
    },
    previousEntry: 'Previous video in the queue',
    nextEntry: 'Next video in the queue',
    previousLabel: 'Previous videos',
    nextLabel: 'More videos',
    nowPlaying: 'Now playing',
    entryGone: 'Video unavailable',
    remove: (title: string): string => `Remove “${title}” from the queue`,
    removeTitle: 'Remove from the queue',
    moveUp: (title: string): string => `Move “${title}” up in the queue`,
    moveUpTitle: 'Move up',
    moveDown: (title: string): string => `Move “${title}” down in the queue`,
    moveDownTitle: 'Move down',
    removing: 'Removing…',
    loading: 'Loading the queue…',
    problem: 'The queue did not load. The video plays either way.',
    /** The panel's heading, the one place on this page that names the list a queue. */
    title: 'Queue',
  },

  /** §7's four menus. Every `value` is what the closed control shows. */
  menu: {
    /**
     * The two controls as one, below the `md` step — the name a screen reader speaks for the
     * merged trigger and the heading over its popover. The value names both halves in the order the
     * sections run, so the closed control still says what is set on each.
     */
    audioAndSubtitles: 'Audio and subtitles',
    audioAndSubtitlesValue: (audio: string, subtitles: string): string => `${audio}, ${subtitles}`,
    quality: {
      label: 'Quality',
      auto: 'Auto',
      autoDetail: 'Adapts to the connection',
      autoAt: (height: number): string => `Auto (${height}p)`,
      height: (height: number): string => `${height}p`,
      /** A track's bitrate, two decimals below a megabit and one above — the shape a menu can read. */
      bitrate: (mbps: string): string => `${mbps} Mbps`,
      codecSection: 'Codec',
      codecAuto: 'Automatic',
      codecForceAvc: 'Force H.264',
      codecForceAvcDetail: 'For hardware that cannot decode AV1',
      noAvcNote: 'This video has no H.264 version, so it plays in the format it does have.',
      pinMissedNote: (height: number): string =>
        `${height}p is not in this video, so the closest one is playing instead.`,
    },
    audio: {
      label: 'Audio track',
      /** §7: `auto` means *the original*, whatever language that is — never the browser locale. */
      original: 'Original',
      /** What the tight trigger prints for the original track, where a chosen language prints its code. */
      originalShort: 'OG',
      /** The original's row, worn like every other language row: its name, then the fact. */
      originalEntry: (language: string): string => `${language} - Original`,
      /** The settings toggle, repeated at the bottom of the menu — the shorter label the layout calls for. */
      stableVolume: 'Use stable volume',
    },
    subtitles: {
      label: 'Subtitles',
      off: 'Off',
      autoGenerated: 'Auto-generated',
    },
    speed: {
      label: 'Playback speed',
      normal: 'Normal',
      rate: (rate: number): string => `${rate}×`,
    },
  },
} as const;
